import {GuestConsentProvider} from '@backstage-components/guest-consent-provider';
import {AttendeeProvider} from '@backstage-components/attendee-container';
import type {AppPage} from '@backstage-components/base';
import {ShowInstructionsProvider} from '@backstage-components/base';
import {FC, PropsWithChildren} from 'react';
import {HelmetProvider} from 'react-helmet-async';
import {client} from './apollo';
import {config} from './config';
import {
  useAnalytics,
  useFetchInstructions,
  type UseFetchInstructionsOptions,
} from './hooks';
import {JsonValue} from 'type-fest';

const analyticsBlockList: string[] = ['xboxfanfestchallenge.com'];

export const AppContext: FC<PropsWithChildren<AppContextProps>> = ({
  appPages,
  children,
  domainName,
  rootElement,
  showControllerType = 'POLL',
  showId = FALLBACK_SHOW_ID,
  siteVariableLookup,
  status,
}) => {
  const instructionFetcher = useFetchInstructions({
    appKey: config.pusherKey,
    cluster: config.pusherCluster,
    showControllerType,
    showId,
    status,
  });
  const analytics = useAnalytics(
    typeof domainName === 'string' &&
      analyticsBlockList.some((host) => domainName.endsWith(host))
      ? 'disabled'
      : 'default'
  );
  return (
    <ShowInstructionsProvider
      allowedEmbedOrigins={['*']}
      analyticsEndpoint={analytics?.endpoint}
      analyticsToken={analytics?.token}
      appPages={appPages}
      domainName={domainName ?? 'unknown'}
      rootElement={rootElement}
      fetchInstructions={instructionFetcher}
      showId={showId}
      siteVariableLookup={siteVariableLookup}
    >
      <AttendeeProvider client={client} showId={showId}>
        <GuestConsentProvider domainName={domainName}>
          <HelmetProvider>{children}</HelmetProvider>
        </GuestConsentProvider>
      </AttendeeProvider>
    </ShowInstructionsProvider>
  );
};

const FALLBACK_SHOW_ID = '00000000-0000-0000-0000-000000000000';

interface AppContextProps
  extends Pick<
    UseFetchInstructionsOptions,
    'showControllerType' | 'showId' | 'status'
  > {
  appPages: AppPage[];
  /** Domain on which the `showId` was viewed */
  domainName?: string;
  /** Element at the root of the application */
  rootElement?: HTMLElement;
  siteVariableLookup: Record<string, JsonValue>;
}
